import React, { useCallback, useMemo, useState } from "react";
import AccountContext from "./AccountContext";
import { IAccountContext } from "./types";

/** localStorage key under which the selected context ID is persisted across page loads. */
const STORAGE_KEY = "curbsuite.accountContextId";

/**
 * Pure-state provider for account context selection.
 *
 * **Responsibilities:**
 * - Initialises `contextId` synchronously from `localStorage["curbsuite.accountContextId"]`
 *   on mount, so `AuthorizedApolloProvider` has the correct value on the very first request.
 * - Exposes `setContextId` and `clearAccountContext` to update state and keep localStorage
 *   in sync.
 *
 * **What this provider does NOT do:**
 * The `AvailableAccountContexts` query and stale-ID validation live in
 * {@link AccountContextInitializer}. Keeping this provider free of any Apollo dependency
 * allows it to be placed **above** `AuthorizedApolloProvider` in the tree, so
 * `AuthorizedApolloProvider` can read the real `contextId` via
 * `React.useContext(AccountContext)` and inject `X-Account-Context-Id` into every
 * outbound GraphQL request.
 *
 * @example
 * ```tsx
 * // apps/curbsuite/src/index.tsx
 * <AccountContextProvider>       // above Apollo — no Apollo dep
 *   <AuthorizedApolloProvider>   // reads real contextId from context
 *     <AccountContextInitializer />
 *     <App />
 *   </AuthorizedApolloProvider>
 * </AccountContextProvider>
 * ```
 */
export const AccountContextProvider: React.FC = ({ children }) => {
  // Initialise optimistically from localStorage so AuthorizedApolloProvider
  // carries the correct header on the very first outbound request.
  const [contextId, setContextIdState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  const setContextId = useCallback((id: string | null) => {
    if (id === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, id);
    }
    setContextIdState(id);
  }, []);

  const clearAccountContext = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setContextIdState(null);
  }, []);

  const value = useMemo<IAccountContext>(
    () => ({ contextId, setContextId, clearAccountContext }),
    [contextId, setContextId, clearAccountContext],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export default AccountContextProvider;
